import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import { AuthUserData, UserType } from '../../models/user';

interface AuthState {
  token: string | null;
  user: AuthUserData;
  // `true` once Firebase auth (via `useAuthObserver`) has made a definitive
  // determination — either "no user" or "user + userData fetched". Stays
  // `false` while the determination is in flight so that `RequireAuth` can
  // hold a spinner instead of redirecting to /login prematurely.
  isResolved: boolean;
}

const dummyUser: AuthUserData = {
  id: '',
  username: 'user',
  type: UserType.COMMON,
  email: 'info@avantti.io',
  phone_number: '',
  user_businesses: [],
  user_events: [],
  photo_url: 'https://via.placeholder.com/40',
};

const initialState: AuthState = {
  token: null,
  user: dummyUser,
  isResolved: false,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (state, action: PayloadAction<{ user: AuthUserData; token: string }>) => {
      const { user, token } = action.payload;
      Cookies.set('token', token);
      Cookies.set('userData', JSON.stringify(user));
      return {
        ...state, user, token, isResolved: true,
      };
    },
    updateToken: (state, action: PayloadAction<string>) => {
      Cookies.set('token', action.payload);
      return { ...state, token: action.payload };
    },
    logout: (state) => {
      Cookies.remove('token');
      Cookies.remove('userData');
      Cookies.remove('selectedBusiness');
      Cookies.remove('selectedBusinessId');
      Cookies.remove('selectedEvent');
      return {
        ...state, user: dummyUser, token: null, isResolved: true,
      };
    },
    authResolved: (state) => ({ ...state, isResolved: true }),
    loadAuth: (state) => {
      const raw = Cookies.get('userData');
      const token = Cookies.get('token');
      if (!raw || !token) {
        return state;
      }
      try {
        const parsed = JSON.parse(raw) as Partial<AuthUserData>;
        // `JSON.parse('{}')` is truthy but useless — require a real id
        // so we never trust a half-empty cookie as a logged-in user.
        if (!parsed.id) {
          return state;
        }
        // Back-fill arrays the rest of the app assumes are always present.
        // Older cookies (and truncated cookies, which happen when a user
        // has many businesses/events and the value exceeds the ~4KB limit)
        // can omit these keys entirely.
        const user: AuthUserData = {
          ...dummyUser,
          ...parsed,
          user_events: parsed.user_events ?? [],
          user_businesses: parsed.user_businesses ?? [],
        };
        return { ...state, user, token };
      } catch {
        return state;
      }
    },
  },
});

export const {
  logout, loginSuccess, loadAuth, updateToken, authResolved,
} = authSlice.actions;

export const authReducer = authSlice.reducer;
